/**
 * Group billing helpers for the partner Billing tab and the traveler invoice.
 * previewSplit mirrors backend/services/groupBillingMath.computeSplit so the
 * split editor can show per-traveler amounts before saving.
 */

export const CHARGE_CATEGORIES = [
  'hotel', 'cruise_fare', 'port_taxes', 'gratuities', 'flight', 'transfer',
  'activity', 'insurance', 'service_fee', 'package', 'other',
];
export const UNIT_BASES = ['per_person', 'per_unit', 'per_group', 'per_night_per_unit', 'per_person_per_night'];
export const SPLIT_METHODS = ['assigned', 'equal', 'weighted', 'fixed', 'occupancy'];
export const COLLECTION_METHODS = ['in_app', 'external_link', 'offline'];
export const MANUAL_PAYMENT_METHODS = ['external_link', 'bank_transfer', 'credit_card', 'check', 'cash', 'other'];
export const UNIT_TYPES = ['hotel_room', 'cruise_cabin', 'other'];
export const TRAVELER_TYPES = ['adult', 'child', 'infant'];

const PER_PERSON = new Set(['per_person', 'per_person_per_night']);
const NIGHTLY = new Set(['per_night_per_unit', 'per_person_per_night']);

/** Quick "Add charge" presets. */
export const CHARGE_TEMPLATES = {
  hotel_room: { category: 'hotel', unitBasis: 'per_night_per_unit', splitMethod: 'equal', nights: 2 },
  cruise_cabin: { category: 'cruise_fare', unitBasis: 'per_unit', splitMethod: 'occupancy' },
  per_person_fee: { category: 'port_taxes', unitBasis: 'per_person', splitMethod: 'assigned', commissionable: false },
  flight: { category: 'flight', unitBasis: 'per_person', splitMethod: 'assigned', dueInFull: true },
  group_item: { category: 'activity', unitBasis: 'per_group', splitMethod: 'equal' },
  custom: { category: 'other', unitBasis: 'per_group', splitMethod: 'equal' },
};

export function emptyChargeForm(template = 'custom', currency = 'USD') {
  return {
    name: '',
    category: 'other',
    unitBasis: 'per_group',
    splitMethod: 'equal',
    quantity: 1,
    nights: 1,
    unitPrice: '',
    unitCost: '',
    supplier: '',
    currency,
    fxRate: 1,
    commissionable: true,
    commissionRate: '',
    refundable: true,
    penaltyAfter: '',
    penaltyAmount: '',
    dueInFull: false,
    dueDate: '',
    inventoryUnitId: '',
    occupancyPricing: { single: '', double: '', third: '', fourth: '' },
    participants: {},
    ...(CHARGE_TEMPLATES[template] || {}),
  };
}

const blank = (v) => (v === null || v === undefined ? '' : v);

export function chargeToForm(charge, currency = 'USD') {
  if (!charge) return emptyChargeForm('custom', currency);
  const participants = {};
  (charge.allocations || []).forEach((a) => {
    participants[a.travelerId] = {
      selected: true,
      weight: blank(a.weight) === '' ? 1 : a.weight,
      fixedAmount: blank(a.fixedAmount),
    };
  });
  const occ = charge.occupancyPricing || {};
  return {
    ...emptyChargeForm('custom', currency),
    name: charge.name,
    category: charge.category,
    unitBasis: charge.unitBasis,
    splitMethod: charge.splitMethod,
    quantity: charge.quantity,
    nights: charge.nights,
    unitPrice: blank(charge.unitPrice),
    unitCost: blank(charge.unitCost),
    supplier: blank(charge.supplier),
    currency: charge.currency || currency,
    fxRate: charge.fxRate || 1,
    commissionable: charge.commissionable,
    commissionRate: blank(charge.commissionRate),
    refundable: charge.refundable,
    penaltyAfter: blank(charge.penaltyAfter),
    penaltyAmount: blank(charge.penaltyAmount),
    dueInFull: charge.dueInFull,
    dueDate: blank(charge.dueDate),
    inventoryUnitId: blank(charge.inventoryUnitId),
    occupancyPricing: {
      single: blank(occ.single), double: blank(occ.double), third: blank(occ.third), fourth: blank(occ.fourth),
    },
    participants,
  };
}

export function selectedParticipants(form) {
  return Object.entries(form.participants || {})
    .filter(([, p]) => p && p.selected)
    .map(([id, p]) => ({ travelerId: Number(id), weight: p.weight, fixedAmount: p.fixedAmount }));
}

export function chargeFormToPayload(form) {
  const occupancy = {};
  Object.entries(form.occupancyPricing || {}).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) occupancy[k] = Number(v);
  });
  return {
    name: form.name.trim(),
    category: form.category,
    unitBasis: form.unitBasis,
    splitMethod: form.splitMethod,
    quantity: Number(form.quantity) || 1,
    nights: Number(form.nights) || 1,
    unitPrice: form.unitPrice === '' ? null : Number(form.unitPrice),
    unitCost: form.unitCost === '' ? null : Number(form.unitCost),
    supplier: form.supplier || null,
    currency: form.currency,
    fxRate: Number(form.fxRate) || 1,
    commissionable: Boolean(form.commissionable),
    commissionRate: form.commissionRate === '' ? null : Number(form.commissionRate),
    refundable: Boolean(form.refundable),
    penaltyAfter: form.penaltyAfter || null,
    penaltyAmount: form.penaltyAmount === '' ? null : Number(form.penaltyAmount),
    dueInFull: Boolean(form.dueInFull),
    dueDate: form.dueDate || null,
    inventoryUnitId: form.inventoryUnitId ? Number(form.inventoryUnitId) : null,
    occupancyPricing: form.splitMethod === 'occupancy' ? occupancy : null,
    participants: selectedParticipants(form),
  };
}

const toCents = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

function lineCents(form, unit = form.unitPrice) {
  const qty = Math.max(1, parseInt(form.quantity, 10) || 1);
  const nights = NIGHTLY.has(form.unitBasis) ? Math.max(1, parseInt(form.nights, 10) || 1) : 1;
  const fx = Number(form.fxRate) > 0 ? Number(form.fxRate) : 1;
  return Math.round(toCents(unit) * qty * nights * fx);
}

function distribute(total, weights, people) {
  const sum = weights.reduce((s, w) => s + w, 0);
  if (sum <= 0) return weights.map(() => 0);
  const shares = weights.map((w) => Math.floor((total * w) / sum));
  let left = total - shares.reduce((s, v) => s + v, 0);
  const order = people
    .map((p, i) => ({ i, id: Number(p.id), lead: Boolean(p.isLead) }))
    .sort((a, b) => (Number(b.lead) - Number(a.lead)) || (a.id - b.id))
    .map((x) => x.i)
    .filter((i) => weights[i] > 0);
  let c = 0;
  while (left > 0 && order.length) {
    shares[order[c % order.length]] += 1;
    left -= 1;
    c += 1;
  }
  return shares;
}

/**
 * people: [{ id, isLead, travelerType, weight, fixedAmount }]
 * Returns { totalCents, rows: [{ id, amountCents }], fixedMismatchCents }.
 */
export function previewSplit(form, people = []) {
  const unit = lineCents(form);
  const n = people.length;
  if (!n) return { totalCents: 0, rows: [], fixedMismatchCents: 0 };
  const perPerson = PER_PERSON.has(form.unitBasis);
  const method = form.splitMethod;
  if (method === 'occupancy') {
    const rank = { adult: 0, child: 1, infant: 2 };
    const ordered = [...people].sort((a, b) => ((Number(a.berth) || 0) - (Number(b.berth) || 0))
      || ((rank[a.travelerType] || 0) - (rank[b.travelerType] || 0)) || (Number(a.id) - Number(b.id)));
    const occ = form.occupancyPricing || {};
    const has = (k) => occ[k] !== '' && occ[k] !== undefined && occ[k] !== null;
    const double = has('double') ? occ.double : form.unitPrice;
    let rows;
    if (n === 1) {
      rows = [{ id: ordered[0].id, amountCents: lineCents(form, has('single') ? occ.single : double) }];
    } else {
      const pair = distribute(lineCents(form, double), [1, 1], ordered.slice(0, 2));
      rows = ordered.slice(0, 2).map((p, i) => ({ id: p.id, amountCents: pair[i] }));
      ordered.slice(2).forEach((p, i) => {
        const half = Number(double) / 2;
        const price = i === 0 ? (has('third') ? occ.third : half) : (has('fourth') ? occ.fourth : (has('third') ? occ.third : half));
        rows.push({ id: p.id, amountCents: lineCents(form, price) });
      });
    }
    return { totalCents: rows.reduce((s, r) => s + r.amountCents, 0), rows, fixedMismatchCents: 0 };
  }
  if (method === 'fixed') {
    const rows = people.map((p) => ({ id: p.id, amountCents: toCents(p.fixedAmount) }));
    const total = rows.reduce((s, r) => s + r.amountCents, 0);
    return { totalCents: total, rows, fixedMismatchCents: perPerson ? 0 : unit - total };
  }
  const weight = (p) => {
    if (method !== 'weighted') return 1;
    const w = Number(p.weight);
    return Number.isFinite(w) && w >= 0 ? w : 1;
  };
  if (perPerson || method === 'assigned') {
    const rows = people.map((p) => ({ id: p.id, amountCents: Math.round(unit * weight(p)) }));
    return { totalCents: rows.reduce((s, r) => s + r.amountCents, 0), rows, fixedMismatchCents: 0 };
  }
  const shares = distribute(unit, people.map(weight), people);
  return { totalCents: unit, rows: people.map((p, i) => ({ id: p.id, amountCents: shares[i] })), fixedMismatchCents: 0 };
}

const HTTPS_URL = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#]\S*)?$/i;

export function isHttpsUrl(value) {
  const text = String(value || '').trim();
  return text.length <= 500 && HTTPS_URL.test(text);
}

export const BILLING_STATUS_SX = {
  no_charges: { borderColor: '#CFC8BC', color: '#5C4A4E' },
  not_invoiced: { borderColor: '#ed6c02', color: '#ed6c02' },
  invoiced: { borderColor: '#0288d1', color: '#0288d1' },
  sent: { borderColor: '#0288d1', color: '#0288d1' },
  partially_paid: { borderColor: '#0288d1', color: '#0288d1' },
  paid: { borderColor: '#2e7d32', color: '#2e7d32' },
  overdue: { borderColor: '#7A1F2B', color: '#7A1F2B' },
  void: { borderColor: '#CFC8BC', color: '#5C4A4E' },
  due: { borderColor: '#ed6c02', color: '#ed6c02' },
};

export function centsToAmount(cents) {
  return Math.round(Number(cents) || 0) / 100;
}

/** Trigger a browser download for a blob response. */
export function downloadBlob(data, filename, type) {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
